import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  UntypedFormArray,
  UntypedFormControl,
  Validators,
  FormGroup,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { FullFeedbackAlertType } from 'src/app/models/enums/full-feedback-alert-type';
import { LocalDocumentType } from 'src/app/models/enums/hospital-document.enum';
import { RESPONSE_STATUS } from 'src/app/models/enums/response-status.enum';
import { FileViewerDialogComponent } from '../file-viewer-dialog/file-viewer-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { LoadingService } from 'src/app/services/loading.service';
import { HospitalContractService } from 'src/app/services/hospital-contract.service';

export interface FullFeedbackData {
  type: FullFeedbackAlertType;
  title: string;
  messages: string[];
  error_msg?: string[];
  confirm?: boolean;
  positive?: string;
  positiveIcon?: string;
  negative?: string;
  negativeIcon?: string;
  requiredReason?: boolean;
  requiredAttachment?: boolean;
  deferUpload?: boolean; // If true, don't upload files immediately, return raw files on close
}
@Component({
  selector: 'app-confirm-dialog-attachment',
  templateUrl: './confirm-dialog-attachment.component.html',
  styleUrl: './confirm-dialog-attachment.component.scss',
})
export class ConfirmDialogAttachmentComponent implements OnInit {
  readonly ENT_DOC_TYPE = LocalDocumentType;
  descriprion: UntypedFormControl;
  formGroup!: FormGroup;
  rawFiles: File[] = []; // Store raw files when deferUpload is true
  readonly TYPE = FullFeedbackAlertType; // FOR NO MODIFY TO THE ENUM
  constructor(
    private _cd: ChangeDetectorRef,
    private hospitalContractService: HospitalContractService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private _loadingService: LoadingService,

    private dialogRef: MatDialogRef<ConfirmDialogAttachmentComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FullFeedbackData,
  ) {
    this.descriprion = new UntypedFormControl(null, Validators.required);
  }

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      documents: this.fb.array([]),
    });
    this.initialize();
  }

  async initialize(): Promise<void> {
    this.dialogRef.addPanelClass([
      'animate__animated',
      'animate__slideInUp',
      'animate__faster',
    ]);
    await this.dialogRef.beforeClosed().toPromise();
    this.dialogRef.removePanelClass([
      'animate__animated',
      'animate__slideInUp',
      'animate__faster',
    ]);
    this.dialogRef.addPanelClass([
      'animate__animated',
      'animate__fadeOutDown',
      'animate__faster',
    ]);
  }

  async onUploadDocument(
    e: Event,
    doctype: LocalDocumentType,
    formArray: UntypedFormArray,
  ) {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    if (formArray.length > 0) {
      formArray.clear();
    }

    if (this.data.deferUpload) {
      this.rawFiles = [file];
      formArray.push(
        this.fb.control(
          { filename: file.name, contentType: file.type, file: file },
          Validators.required,
        ),
      );
      input.value = '';
      formArray.markAsDirty();
      this._cd.markForCheck();
      return;
    }

    const response = await lastValueFrom(
      this.hospitalContractService.uploadAttachment(input.files, doctype),
    );

    input.value = '';
    if (response.status !== RESPONSE_STATUS.SUCCESS) return;

    const uploadedFile = Array.isArray(response.data)
      ? response.data[0]
      : response.data;

    formArray.push(
      this.fb.control(
        {
          filename: uploadedFile.filename,
          contentType: uploadedFile.contentType,
          _id: uploadedFile._id,
          type: uploadedFile.metadata.type,
        },
        Validators.required,
      ),
    );

    formArray.markAsDirty();
    this._cd.markForCheck();
  }

  deleteFile(formArray: UntypedFormArray) {
    if (formArray.length > 0) {
      formArray.clear();
    }

    this.rawFiles = [];

    formArray.markAsDirty();
    this._cd.markForCheck();
  }

  getUploadedFileTitle(formArray: UntypedFormArray): string {
    if (!formArray || formArray.length === 0) {
      return '';
    }

    if (formArray.length === 1) {
      return formArray.at(0).value?.filename ?? '';
    }

    return `${formArray.length} files uploaded`;
  }

  getFileIcon(element: any): string {
    if (!element) return 'insert_drive_file';

    let type = '';

    if (typeof element === 'string') {
      type = element;
    }

    if (typeof element === 'object') {
      type = element.contentType || element.type || element.filename || '';
    }

    if (type === 'application/pdf' || type.endsWith('.pdf')) {
      return 'picture_as_pdf';
    }

    if (type.startsWith('image/') || /\.(png|jpg|jpeg)$/i.test(type)) {
      return 'image';
    }

    return 'insert_drive_file';
  }

  get supporting_docs(): UntypedFormArray {
    return this.formGroup.controls.documents as UntypedFormArray;
  }

  onSubmit() {
    if (this.data.requiredReason && !this.descriprion.valid) return;
    if (
      this.data.requiredAttachment &&
      (!this.formGroup.value.documents ||
        this.formGroup.value.documents.length === 0)
    )
      return;

    this.dialogRef.close({
      description: this.descriprion.value,
      documents: this.formGroup.value.documents,
      rawFiles: this.data.deferUpload ? this.rawFiles : undefined,
    });
  }

  getFile(id: string): void {
    this.hospitalContractService.getAttachment(id).subscribe((response) => {
      if (!response) return;
      this._onViewPDF(response);
    });
  }

  private async _onViewPDF(response: Blob) {
    let url = window.URL.createObjectURL(response);
    await lastValueFrom(
      this.dialog
        .open(FileViewerDialogComponent, {
          width: '850px',
          autoFocus: false,
          backdropClass: 'file-viewer',
          data: {
            url: url,
            name: '',
            type: response.type,
          },
        })
        .afterClosed(),
    );
    URL.revokeObjectURL(url);
  }

  // async deleteFile(mainControl: UntypedFormArray, index: number) {
  //   if (
  //     !(await lastValueFrom(
  //       this.dialog
  //         .open(ConfirmDialogComponent, {
  //           width: '500px',
  //           disableClose: true,
  //           autoFocus: false,
  //           data: {
  //             title: 'dialog.confirm.alert',
  //             confirm_desc: `dialog.confirm.remove_data`,
  //             confirm_type: CONFIRM_TYPE.NORMAL,
  //             is_reject: true,
  //           },
  //         })
  //         .afterClosed(),
  //     ))
  //   )
  //     return;
  //   const id = mainControl.controls[index].value._id;

  //   if (this.data.deferUpload) {
  //     this.rawFiles.splice(index, 1);
  //     mainControl.removeAt(index);
  //     return;
  //   }

  //   mainControl.removeAt(index);
  //   const response = await lastValueFrom(
  //     this.hospitalContractService.deleteDocument(id),
  //   );
  //   if (response.status !== RESPONSE_STATUS.SUCCESS) {
  //     return;
  //   }
  // }

  async viewFile(file: any) {
    if (!file) return;

    if (file.file instanceof File) {
      await this._onViewPDF(file.file);
      return;
    }

    if (file._id) {
      this.hospitalContractService
        .getAttachment(file._id)
        .subscribe((response) => {
          if (!response) return;
          this._onViewPDF(response);
        });
    }
  }

  downloadDocument(id: string, name: string): void {
    this.hospitalContractService.getAttachment(id).subscribe((response) => {
      if (!response) return;
      let url = URL.createObjectURL(response);
      this._forceDownload(url, name);
      URL.revokeObjectURL(url);
    });
  }
  private _forceDownload(href: any, name: string) {
    this._loadingService.setLoading(false);
    var anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = name;
    anchor.click();
  }
}
